import { useRef, useState } from "react";
import { Download, Plus, Send, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, getToken } from "../lib/api";
import { Field, Panel } from "./common";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { DateDMY } from "./DateDMY";

const today = () => new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Rome" });
const EMPTY = { nominativo: "", targa: "", email: "", telefono: "" };
const COLS = [["nominativo", "Nome Ospite"], ["targa", "Targa (facoltativa)"], ["email", "Email"], ["telefono", "Cellulare (facoltativo)"]];

async function downloadTemplate() {
  const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/web/parking/bulk/template`, { headers: { Authorization: `Bearer ${getToken()}` } });
  if (!res.ok) return toast.error("Modello non disponibile");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(await res.blob()); a.download = "modello_richiesta_parcheggi.xlsx"; a.click();
}

function Grid({ rows, setRows }) {
  const set = (i, k, v) => setRows(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  return (
    <div className="overflow-x-auto rounded-xl border" data-testid="bulk-grid">
      <table className="w-full text-sm">
        <thead className="bg-slate-50"><tr><th className="w-10 px-2 py-2 text-slate-400">#</th>{COLS.map(([, l]) => <th key={l} className="px-2 py-2 text-left text-slate-600 font-semibold">{l}</th>)}<th /></tr></thead>
        <tbody>{rows.map((r, i) => (
          <tr key={i} className="border-t" data-testid={`bulk-row-${i}`}>
            <td className="px-2 text-center text-slate-400">{i + 1}</td>
            {COLS.map(([k]) => <td key={k} className="p-1"><Input value={r[k]} onChange={(e) => set(i, k, e.target.value)} className={`h-9 rounded-md ${k === "targa" ? "uppercase font-mono" : ""}`} data-testid={`bulk-${k}-${i}`} /></td>)}
            <td className="p-1"><Button size="sm" variant="ghost" onClick={() => setRows(rows.length > 1 ? rows.filter((_, j) => j !== i) : [EMPTY])} data-testid={`bulk-del-${i}`}><Trash2 className="w-4 h-4 text-[var(--red)]" /></Button></td>
          </tr>))}
        </tbody>
      </table>
    </div>
  );
}

export function ParkBulk({ me }) {
  const [f, setF] = useState({ dal: today(), al: today(), turno: "mattina", ospitante: me.ospite_default, messaggio_id: me.default_msg || "" });
  const [rows, setRows] = useState([EMPTY]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const file = useRef(null);
  const upload = (e) => {
    const fl = e.target.files?.[0]; e.target.value = "";
    if (!fl) return;
    const rd = new FileReader();
    rd.onload = async () => {
      try { const d = await api("/web/parking/bulk/parse", { method: "POST", body: { filename: fl.name, data: rd.result } }); if (!d.rows.length) return toast.error("Nessun ospite nel file"); setRows(d.rows); toast.success(`${d.rows.length} ospiti caricati: controlla e invia`); } catch (err) { toast.error(err.message); }
    };
    rd.readAsDataURL(fl);
  };
  const send = async () => {
    const list = rows.filter((r) => Object.values(r).some((v) => v.trim()));
    if (!list.length) return toast.error("Inserisci almeno un ospite");
    setBusy(true);
    try { const d = await api("/web/parking/bulk", { method: "POST", body: { ...f, rows: list } }); setDone(d); setRows([EMPTY]); toast.success("Richieste inserite: email in invio"); } catch (e) { toast.error(e.message); }
    setBusy(false);
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Panel className="space-y-5" data-testid="park-bulk">
      <h2 className="font-display text-lg font-bold text-[var(--navy)]">Richiesta multipla</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <Field label="Dal"><DateDMY min={today()} value={f.dal} onChange={(v) => setF({ ...f, dal: v, al: f.al < v ? v : f.al })} testid="bulk-dal" /></Field>
        <Field label="Al"><DateDMY min={f.dal} value={f.al} onChange={(v) => setF({ ...f, al: v })} testid="bulk-al" /></Field>
        <Field label="Nome utente ospitante"><Input value={f.ospitante} onChange={set("ospitante")} data-testid="bulk-ospitante" /></Field>
        <Field label="Orario"><select className="h-10 w-full rounded-md border px-3 bg-white" value={f.turno} onChange={set("turno")} data-testid="bulk-turno">{Object.entries(me.turni).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></Field>
        <Field label="Messaggio"><select className="h-10 w-full rounded-md border px-3 bg-white" value={f.messaggio_id} onChange={set("messaggio_id")} data-testid="bulk-msg">{me.messaggi.map((m) => <option key={m.id} value={m.id}>{m.nome}</option>)}</select></Field>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" className="rounded-full" onClick={downloadTemplate} data-testid="bulk-template"><Download className="w-4 h-4 mr-1" />Scarica modello Excel</Button>
        <Button variant="outline" className="rounded-full" onClick={() => file.current?.click()} data-testid="bulk-upload"><Upload className="w-4 h-4 mr-1" />Carica file XLS</Button>
        <input ref={file} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={upload} data-testid="bulk-file-input" />
      </div>
      <Grid rows={rows} setRows={setRows} />
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="outline" className="rounded-full" onClick={() => setRows([...rows, EMPTY])} data-testid="bulk-add-row"><Plus className="w-4 h-4 mr-1" />Aggiungi riga</Button>
        <Button onClick={send} disabled={busy} className="rounded-full bg-[var(--navy)]" data-testid="bulk-submit"><Send className="w-4 h-4 mr-1" />{busy ? "Inserimento…" : "Invia richieste ed email"}</Button>
      </div>
      <p className="text-xs text-slate-500">Per ogni ospite viene creata una richiesta per ogni giorno del periodo e parte in automatico un'email con il QR Code, valido tutti i giorni: dopo l'ingresso torna attivo il giorno successivo.</p>
      {done && <p className="rounded-xl bg-green-50 text-green-800 p-3 font-medium" data-testid="bulk-result">Inserite {done.richieste} richieste: {done.ospiti} ospiti per {done.giorni} giorni (dal {done.dal} al {done.al}). Le email partono in automatico.</p>}
    </Panel>
  );
}
